import { useInfiniteQuery, type QueryKey } from "@tanstack/react-query";
import { useMemo } from "react";
import { ApiError, get } from "@/lib/api";
import { DEFAULT_REFETCH_MS } from "./useApi";
import type { Page } from "@/types/fleet";

/**
 * Keyset pagination over `{items, next_cursor}`.
 *
 * The API returns **no total count** anywhere — deliberately, because counting
 * rows behind a filter is the expensive half of the query and nothing in this
 * console needs the number. So there is no page count, no "of 412", and no
 * numbered pager: only 載入更多 and whether a cursor came back.
 */
export function useKeysetQuery<T>(
  key: QueryKey,
  /** Build the path for a page; `cursor` is null for the first one. */
  pathFor: (cursor: string | null) => string,
  opts?: { enabled?: boolean; refetchInterval?: number | false },
) {
  const query = useInfiniteQuery<Page<T>, ApiError, Page<T>[], QueryKey, string | null>({
    queryKey: key,
    queryFn: ({ pageParam }) => get<Page<T>>(pathFor(pageParam)),
    initialPageParam: null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    select: (d) => d.pages,
    enabled: opts?.enabled,
    refetchInterval: opts?.refetchInterval ?? DEFAULT_REFETCH_MS,
  });

  const items = useMemo(() => (query.data ?? []).flatMap((p) => p.items), [query.data]);

  return {
    ...query,
    items,
    /** True while the *first* page is loading — drives the skeleton. */
    isInitialLoading: query.isPending,
    canLoadMore: query.hasNextPage === true,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}
